import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiGithub, FiExternalLink } from 'react-icons/fi';

const PROJECTS = [
  {
    title: 'WinaContract',
    description: 'UK Public Procurement Search Engine. A centralised full-stack platform for UK public procurement opportunities, ingesting and parsing OCDS data from Contracts Finder and FTS APIs. Includes CI/CD pipelines and cloud deployment.',
    tags: ['Python', 'Django', 'PostgreSQL', 'React', 'TypeScript', 'Elasticsearch', 'Docker'],
    github: 'https://github.com/Esosa-web',
    live: null,
  },
  {
    title: 'Axion Solutions Website',
    description: 'Company website for Axion Solutions built with Next.js and configured with Payload CMS as a headless content management system.',
    tags: ['Next.js', 'Payload CMS', 'TypeScript', 'Tailwind CSS'],
    github: null,
    live: null,
  },
  {
    title: 'BrewBuddy',
    description: 'Brewery Discovery Platform. A responsive React application consuming multiple brewery APIs, handling 1000+ brewery data points with interactive maps and filtering.',
    tags: ['React', 'JavaScript', 'Mapbox', 'REST APIs'],
    github: 'https://github.com/Esosa-web',
    live: null,
  },
];

function ProjectCard({ project, index, hoveredIndex, onHover, onLeave }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const isHovered = hoveredIndex === index;
  const isDimmed = hoveredIndex !== null && !isHovered;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={onLeave}
      style={{
        padding: '20px',
        borderRadius: '8px',
        backgroundColor: isHovered ? 'rgba(30, 41, 59, 0.5)' : 'transparent',
        border: `1px solid ${isHovered ? 'rgba(96, 165, 250, 0.08)' : 'transparent'}`,
        opacity: isDimmed ? 0.5 : 1,
        transition: 'background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease',
        cursor: 'default',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <h3
          style={{
            fontSize: '16px',
            fontWeight: 500,
            color: isHovered ? '#60a5fa' : '#e2e8f0',
            transition: 'color 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {project.title}
          {isHovered && (
            <span
              style={{
                display: 'inline-block',
                transform: 'translate(2px, -2px)',
                transition: 'transform 0.2s ease',
                fontSize: '14px',
              }}
            >
              ↗
            </span>
          )}
        </h3>
        <div style={{ display: 'flex', gap: '12px', marginLeft: '16px', flexShrink: 0 }}>
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#94a3b8', transition: 'color 0.2s ease' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#e2e8f0')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
            >
              <FiGithub size={18} />
            </a>
          )}
          {project.live && (
            <a
              href={project.live}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#94a3b8', transition: 'color 0.2s ease' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#e2e8f0')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
            >
              <FiExternalLink size={18} />
            </a>
          )}
        </div>
      </div>

      <p style={{ fontSize: '14px', lineHeight: 1.65, color: '#94a3b8', marginBottom: '16px' }}>
        {project.description}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {project.tags.map((tag) => (
          <span
            key={tag}
            style={{
              padding: '4px 12px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontFamily: 'monospace',
              backgroundColor: 'rgba(96, 165, 250, 0.1)',
              color: '#60a5fa',
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

export default function Projects() {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section id="projects" style={{ marginBottom: '128px' }}>
      <h2
        style={{
          fontSize: '12px',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#60a5fa',
          marginBottom: '40px',
        }}
      >
        Projects
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {PROJECTS.map((project, i) => (
          <ProjectCard
            key={i}
            project={project}
            index={i}
            hoveredIndex={hoveredIndex}
            onHover={setHoveredIndex}
            onLeave={() => setHoveredIndex(null)}
          />
        ))}
      </div>
    </section>
  );
}
